import { ref, watch } from "vue";
import type { RouteLocationNormalized, RouteLocationNormalizedLoaded, RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory, isNavigationFailure, NavigationFailureType } from "vue-router";
import * as Sentry from "@sentry/vue";

import { ConstructionFormRoute } from "./constructionRoutes";

import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import { useProject } from "@stores/project/project.store";
import { useBreadcrumb } from "@/features/Breadcrumb";
import { useSpeckleStore } from "@/stores/speckle/speckle.store";
import type { BreadcrumbType } from "@/features/Breadcrumb/types/BreadcrumbType";
import useHasuraStore from "@/stores/hasura/hasura.store";
import { HasuraNotAuthenticatedError } from "@/stores/hasura/hasuraErrors";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import type { AuthState } from "@frontegg/redux-store";

type AuthStateUser = NonNullable<AuthState["user"]>;
import ProjectsPage from "@/pages/Projects/pages/ProjectsPage.vue";
import { trackProjectView } from "@/composables/useRecentProjectsTracker";
import { readCachedMapping } from "@/composables/useProjectMappingStatus.composable";
import { recoverFromStaleAsset } from "@/services/staleAssetRecovery";

const PROJECT_VIEW_ROUTE_NAMES = [
  "projectBreakdown",
  "materialMappingView",
  "transportMapping",
  "wasteSorting",
  // Opening a project's LCA report is a project view like any other, so it feeds the recents the
  // top-bar switcher lists.
  "projectLcaPdfReportPage",
];

// Retry a dynamic import once after a short delay — a transient chunk/CSS-preload failure
// otherwise aborts the whole navigation until the user hard-refreshes.
function lazyWithRetry<T>(importer: () => Promise<T>, retries = 1, delayMs = 350): () => Promise<T> {
  return async () => {
    try {
      return await importer();
    } catch (error) {
      if (retries <= 0) throw error;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return lazyWithRetry(importer, retries - 1, delayMs)();
    }
  };
}

// Routes that need at least one mapped material (Transport, Waste); redirect to Material Mapping on
// direct URL/reload when nothing is mapped — nav disables these items, but that misses direct nav.
const MAPPING_REQUIRED_ROUTES = ["transportMapping", "wasteSorting"];

// Reads the mapping cache SYNCHRONOUSLY (never awaits) and redirects only on a definite "not mapped".
// Consumed via next() in beforeEach, not beforeEnter — a beforeEnter redirect here left navigation hanging.
function mappingRequiredRedirect(to: RouteLocationNormalized) {
  if (!MAPPING_REQUIRED_ROUTES.includes(to.name as string)) return null;
  const projectId = String(to.params.id ?? "");
  if (projectId && readCachedMapping(projectId) === false) {
    return { name: "materialMappingView", params: { id: projectId } };
  }
  return null;
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "projects",
    component: ProjectsPage,
    meta: {
      breadcrumb: () => [{ label: "Projects" }],
    },
  },

  {
    // Shareable compare page: /projects/compare?ids=a,b,c — mirrors /library/compare.
    path: "/projects/compare",
    name: "projectCompare",
    component: lazyWithRetry(
      () => import(/* webpackChunkName: "projectCompare" */ "@/pages/Projects/pages/ProjectComparePage.vue")
    ),
    meta: {
      breadcrumb: () => [{ label: "Projects", to: { name: "projects" } }, { label: "Compare" }],
    },
  },

  {
    path: "/agent",
    name: "agent",
    component: () => import(/* webpackChunkName: "agent" */ "../views/Agent/AgentView.vue"),
    // /agent and /agent/skills render the same component; the child inherits this title because
    // vue-router merges meta from every matched record into to.meta.
    meta: {
      title: "AI Agent Skills",
    },
    children: [
      {
        path: "skills",
        name: "skills",
        component: () => import(/* webpackChunkName: "skills" */ "../views/Agent/AgentView.vue"),
      },
    ],
  },

  {
    path: "/agent/usage",
    name: "agentUsage",
    component: () => import(/* webpackChunkName: "agent-usage" */ "@/features/AgentUsage/AgentUsagePage.vue"),
    meta: {
      title: "AI Agent Usage",
      breadcrumb: () => [{ label: "Usage" }],
    },
  },

  {
    path: "/agent/usage/history",
    name: "agentUsageHistory",
    component: () => import(/* webpackChunkName: "agent-usage-history" */ "@/features/AgentUsageHistory/HistoryPage.vue"),
    meta: {
      title: "AI Agent Usage History",
      breadcrumb: () => [{ label: "Usage", to: { name: "agentUsage" } }, { label: "History" }],
    },
  },

  {
    path: "/account/forget-password",
    name: "forgotPassword",
    component: () => import(/* webpackChunkName: "forget-password" */ "../views/Utils/ForgotPassword.vue"),
  },
  {
    path: "/account/login",
    name: "login",
    component: () => import(/* webpackChunkName: "login" */ "../views/Utils/LoginRedirect.vue"),
  },
  {
    path: "/account/logout",
    name: "logout",
    component: () => import(/* webpackChunkName: "logout" */ "../views/Utils/Logout.vue"),
  },
  {
    path: "/admin",
    name: "adminPanel",
    component: () => import(/* webpackChunkName: "admin" */ "../views/AdminPanelTenantOverview.vue"),
    meta: {
      requiresPlatformAdmin: true,
      title: "Admin Panel",
      breadcrumb: () => [{ label: "Admin Panel" }],
    },
  },
  {
    path: "/admin/:id",
    name: "tenantBreakdown",
    component: () => import(/* webpackChunkName: "admin" */ "../views/AdminPanelTenantBreakdown.vue"),
    meta: {
      requiresPlatformAdmin: true,
      breadcrumb: (to: RouteLocationNormalizedLoaded) => [
        { label: "Admin Panel", to: { name: "adminPanel" } },
        { label: `Tenant ${to.params.id}` },
      ],
    },
  },
  {
    path: "/tutorials",
    name: "tutorials",
    component: () => import(/* webpackChunkName: "tutorials" */ "../views/Tutorials.vue"),
    meta: {
      title: "Tutorials",
      breadcrumb: () => [{ label: "Tutorials" }],
    },
  },
  {
    path: "/library",
    component: () => import("@/layouts/ProjectLayout.vue"),
    children: [
      {
        // Optional Materials/Constructions tab as a route param so the active tab is deep-linkable. Kept
        // under the same `materialsDatabase` name + optional param so existing name references still resolve.
        path: ":libraryType(materials|constructions)?",
        name: "materialsDatabase",
        component: () => import(/* webpackChunkName: "library" */ "../views/MaterialsDatabase.vue"),
        meta: {
          // Needed because the component repeatedly router.replaces on this same route (tab/search/open-item
          // sync) without remounting — afterEach re-fires each time and falls back to the generic title without it.
          title: "Library",
          breadcrumb: () => [{ label: "Library" }],
        },
      },
    ],
  },
  {
    // Shareable compare page: /library/compare?ids=a,b,c. Static path, so it ranks above the
    // dynamic /library/:id below and the (materials|constructions) tab param never matches "compare".
    path: "/library/compare",
    name: "libraryCompare",
    component: () => import("../views/Library/LibraryComparePage.vue"),
    meta: {
      // Same reasoning as materialsDatabase: handleRemove does a same-route router.replace on item
      // removal, which re-fires afterEach and would clobber the title back to generic without this.
      title: "Compare",
      breadcrumb: () => [{ label: "Library", to: { name: "materialsDatabase" } }, { label: "Compare" }],
    },
  },
  {
    path: "/library/:id",
    name: "libraryDetail",
    component: () => import("../views/Library/LibraryDetail.vue"),
    meta: {
      title: "Material Detail",
      breadcrumb: () => [{ label: "Library", to: { name: "materialsDatabase" } }, { label: "Material Detail" }],
    },
  },
  {
    // Construction create/edit/clone live under /library/constructions/* for a consistent hierarchy —
    // their breadcrumbs already read "Library". Standalone top-level routes so they render ConstructionUpsert directly.
    path: "/library/constructions/new",
    name: ConstructionFormRoute.Create,
    component: () => import("../views/Library/Construction/ConstructionUpsert.vue"),
    props: { mode: "create" },
    meta: {
      title: "Create construction",
      breadcrumb: () => [{ label: "Library" }, { label: "Create construction" }],
    },
  },
  {
    path: "/library/constructions/:id/edit",
    name: ConstructionFormRoute.Edit,
    component: () => import("../views/Library/Construction/ConstructionUpsert.vue"),
    props: { mode: "edit" },
    meta: {
      title: "Edit construction",
      breadcrumb: () => [{ label: "Library" }, { label: "Edit construction" }],
    },
  },
  {
    path: "/library/constructions/:id/clone",
    name: ConstructionFormRoute.Clone,
    component: () => import("../views/Library/Construction/ConstructionUpsert.vue"),
    props: { mode: "clone" },
    meta: {
      title: "Clone construction",
      breadcrumb: () => [{ label: "Library" }, { label: "Clone construction" }],
    },
  },
  // Redirect the old top-level /constructions/* paths to their new /library/constructions/* homes
  // so existing links, bookmarks and history entries keep working. Preserve :id + query.
  { path: "/constructions/new", redirect: { name: ConstructionFormRoute.Create } },
  {
    path: "/constructions/:id/edit",
    redirect: (to) => ({ name: ConstructionFormRoute.Edit, params: { id: to.params.id }, query: to.query }),
  },
  {
    path: "/constructions/:id/clone",
    redirect: (to) => ({ name: ConstructionFormRoute.Clone, params: { id: to.params.id }, query: to.query }),
  },

  {
    path: "/library/create-material",
    name: "createMaterial",
    component: () => import(/* webpackChunkName: "CreateMaterial" */ "../views/CreateMaterial.vue"),
    meta: {
      title: "Create Material",
      breadcrumb: () => [{ label: "Library", to: { name: "materialsDatabase" } }, { label: "Create Material" }],
    },
  },
  {
    path: "/project",
    component: () => import("@/layouts/ProjectLayout.vue"),
    children: [
      {
        path: ":id",
        component: () => import("@/layouts/ProjectDetailLayout.vue"),
        children: [
          {
            path: "",
            name: "projectBreakdown",
            component: lazyWithRetry(() => import(/* webpackChunkName: "projectBreakdown" */ "@/pages/Dashboard.vue")),
            meta: {
              breadcrumb: (to: RouteLocationNormalizedLoaded) => [
                { label: "Projects", to: { name: "projects" } },
                { label: `Project ${to.params.id}` },
              ],
            },
          },
          {
            path: "mapping",
            name: "materialMappingView",
            component: lazyWithRetry(
              () => import(/* webpackChunkName: "materialMappingView" */ "../views/MaterialMappingView.vue")
            ),
            meta: {
              breadcrumb: (to: RouteLocationNormalizedLoaded) => [
                { label: "Projects", to: { name: "projects" } },
                {
                  label: `Project ${to.params.id}`,
                  to: { name: "projectBreakdown", params: { id: to.params.id } },
                },
                { label: "Material mapping" },
              ],
            },
          },
          {
            path: "lca-report",
            name: "projectLcaPdfReportPage",
            component: () =>
              import(
                /* webpackChunkName: "projectLcaPdfReportPage" */ "../pages/ProjectLcaPdfReport/pages/ProjectLcaPdfReportPage.vue"
              ),
            meta: {
              breadcrumb: (to: RouteLocationNormalizedLoaded) => [
                { label: "Projects", to: { name: "projects" } },
                {
                  label: `Project ${to.params.id}`,
                  to: { name: "projectBreakdown", params: { id: to.params.id } },
                },
                { label: "LCA report" },
              ],
            },
          },
          {
            path: "transport-mapping",
            name: "transportMapping",
            component: lazyWithRetry(() => import(/* webpackChunkName: "transportMapping" */ "@/pages/TransportMapping.vue")),
            meta: {
              breadcrumb: (to: RouteLocationNormalizedLoaded) => [
                { label: "Projects", to: { name: "projects" } },
                {
                  label: `Project ${to.params.id}`,
                  to: { name: "projectBreakdown", params: { id: to.params.id } },
                },
                { label: "Transport mapping" },
              ],
            },
          },
          {
            path: "waste",
            name: "wasteSorting",
            component: lazyWithRetry(
              () => import(/* webpackChunkName: "wasteSorting" */ "@/pages/WasteSorting/pages/WasteSortingPage.vue")
            ),
            meta: {
              breadcrumb: (to: RouteLocationNormalizedLoaded) => [
                { label: "Projects", to: { name: "projects" } },
                {
                  label: `Project ${to.params.id}`,
                  to: { name: "projectBreakdown", params: { id: to.params.id } },
                },
                { label: "Waste" },
              ],
            },
          },
          {
            // The edit wizard's steps render INSIDE the project (not a separate route); sections are
            // addressed by hash (`#standard-values`) since the page's own copy-link buttons hand out that hash, not a param.
            path: "settings",
            name: "projectSettings",
            component: lazyWithRetry(
              () => import(/* webpackChunkName: "projectSettings" */ "@/pages/ProjectSettings/ProjectSettingsPage.vue")
            ),
            meta: {
              breadcrumb: (to: RouteLocationNormalizedLoaded) => [
                { label: "Projects", to: { name: "projects" } },
                {
                  label: `Project ${to.params.id}`,
                  to: { name: "projectBreakdown", params: { id: to.params.id } },
                },
                // Plain text, no `to`: this IS the settings page, and a crumb pointing at it would
                // just bounce back to the page you are reading.
                { label: "Settings" },
              ],
            },
          },
          {
            // Speckle's own settings screen — same component as `settings`, swapping in the integration
            // view — but a ROUTE rather than a hash, so Back returns to Integrations and it's linkable on its own.
            path: "settings/integrations/speckle",
            name: "projectSettingsSpeckle",
            component: lazyWithRetry(
              () => import(/* webpackChunkName: "projectSettings" */ "@/pages/ProjectSettings/ProjectSettingsPage.vue")
            ),
            meta: {
              breadcrumb: (to: RouteLocationNormalizedLoaded) => [
                { label: "Projects", to: { name: "projects" } },
                {
                  label: `Project ${to.params.id}`,
                  to: { name: "projectBreakdown", params: { id: to.params.id } },
                },
                { label: "Settings", to: { name: "projectSettings", params: { id: to.params.id }, hash: "#integrations" } },
                { label: "Speckle" },
              ],
            },
          },
          {
            // Sections are shareable links, so the old `/settings/general` path stays a redirect rather
            // than a 404 for a link someone already sent — same reasoning and hash-carrying as `/config` below.
            path: "settings/general",
            redirect: (to: RouteLocationNormalizedLoaded) => ({
              name: "projectSettings",
              params: to.params,
              hash: to.hash,
            }),
          },
          {
            // Old `/config` path stays a redirect (shareable section links) with `hash` passed EXPLICITLY —
            // a redirect doesn't carry the fragment on its own, so losing it would drop every shared link's section.
            path: "config",
            redirect: (to: RouteLocationNormalizedLoaded) => ({
              name: "projectSettings",
              params: to.params,
              hash: to.hash,
            }),
          },
          {
            // Version history mockup — fabricated audit trail while project
            // versioning (Hasura project_revision + backend restore) is being designed.
            path: "history",
            name: "projectVersionHistory",
            component: lazyWithRetry(
              () =>
                import(
                  /* webpackChunkName: "projectVersionHistory" */ "@/pages/ProjectVersionHistory/pages/ProjectVersionHistoryPage.vue"
                )
            ),
            meta: {
              breadcrumb: (to: RouteLocationNormalizedLoaded) => [
                { label: "Projects", to: { name: "projects" } },
                {
                  label: `Project ${to.params.id}`,
                  to: { name: "projectBreakdown", params: { id: to.params.id } },
                },
                { label: "Version history" },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    path: "/oauth/:afterOauth(.*)",
    name: "loginPath",
    component: () => import(/* webpackChunkName: "auth" */ "../views/Utils/LoginRedirect.vue"),
  },
  {
    path: "/purchase",
    name: "purchase",
    component: () => import(/* webpackChunkName: "purchase" */ "@/pages/Purchase/pages/PurchasePage.vue"),
    meta: {
      requiresPlatformAdmin: true,
      breadcrumb: () => [{ label: "Purchase" }],
    },
  },
  {
    path: "/purchase/result",
    name: "purchaseResult",
    component: () => import(/* webpackChunkName: "purchase" */ "@/pages/Purchase/pages/PurchaseResultPage.vue"),
    meta: {
      requiresPlatformAdmin: true,
      breadcrumb: () => [{ label: "Purchase", to: { name: "purchase" } }, { label: "Payment result" }],
    },
  },
  {
    path: "/purchases/invoices",
    name: "invoices",
    component: () => import(/* webpackChunkName: "invoices" */ "@/pages/Invoices/pages/InvoicesPage.vue"),
    meta: {
      requiresPlatformAdmin: true,
      breadcrumb: () => [{ label: "Purchase", to: { name: "purchase" } }, { label: "Invoices" }],
    },
  },
  {
    // Landing spot for Speckle's authorize redirect: receives ?access_code and finishes the connect flow.
    // This path is registered as the "Real-Time LCA" app's redirect URL on Speckle — changing it needs updating that registration too.
    path: "/integrations/speckle/callback",
    name: "speckleConnectCallback",
    component: () => import(/* webpackChunkName: "speckle-callback" */ "@/pages/Integrations/SpeckleCallbackPage.vue"),
  },
  { path: "/:pathMatch(.*)*", name: "pageNotFound", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

function resolveBreadcrumb(to: RouteLocationNormalizedLoaded): BreadcrumbType[] {
  const meta = to.meta?.breadcrumb as BreadcrumbType[] | ((to: RouteLocationNormalizedLoaded) => BreadcrumbType[]) | undefined;

  if (!meta) return [];
  return typeof meta === "function" ? meta(to) : meta;
}

const SITE_TITLE = "Real-Time LCA";

// Pages used to set document.title on mount, so an untouched page silently inherited the previous
// route's title; resolving it here means a route can never show another route's title.
function resolveTitle(to: RouteLocationNormalizedLoaded): string {
  const meta = to.meta?.title as string | ((to: RouteLocationNormalizedLoaded) => string) | undefined;
  const label = typeof meta === "function" ? meta(to) : meta;
  if (label) return `${label} - ${SITE_TITLE}`;

  // Project name goes in the tab, but only when the loaded project actually matches this route's :id —
  // the same guard the breadcrumb applies below; without it a cached name leaks into the wrong project.
  const projectId = typeof to.params.id === "string" ? to.params.id : undefined;
  if (projectId) {
    const project = useProjectWrapper().getProjectWrapper()?.project;
    if (project?.id === projectId && project.projectName) return `${project.projectName} - ${SITE_TITLE}`;
  }

  return SITE_TITLE;
}

// Reactive variable to store the last route
export const previousRoute = ref(router.currentRoute.value);

export function installGuards(router: ReturnType<typeof createRouter>) {
  router.beforeEach(async (to, from, next) => {
    const projectId = to.params.id as string;

    if (to.path.startsWith("/oauth/")) return next();

    // Purchase, invoices and the admin panel are gaiup-internal pages — only the PlatformAdmin Frontegg role gets in.
    if (to.meta?.requiresPlatformAdmin) {
      const authStateStore = useAuthStateStore();
      const authUser = await waitForAuthUser();

      // No user at all means Frontegg has not signed anyone in yet; its own guard sends them to
      // login and this guard runs again afterwards. Only redirect on a real "signed in, wrong role".
      if (authUser && !authStateStore.isPlatformAdmin) {
        if (import.meta.env.DEV) {
          console.warn(
            "[router] PlatformAdmin route blocked. Roles on the current user:",
            authUser.roles?.map((role) => ({ key: role.key, name: role.name }))
          );
        }

        return next({ name: "projects" });
      }
    }

    const projectWrapper = useProjectWrapper();
    const breadcrumb = useBreadcrumb();

    // Routed through next(), not a bare return: this guard declares `next`, so vue-router rejects a
    // returned location ("Invalid navigation guard") — same constraint applies to the redirect below.
    if (to.hash === "#/admin-box/") {
      return next({ path: to.path, query: to.query, hash: "" });
    }

    // Redirect to Material Mapping when nothing is mapped, routed through next() for the same reason
    // as the admin-box strip above. See mappingRequiredRedirect.
    const mappingRedirect = mappingRequiredRedirect(to);
    if (mappingRedirect) return next(mappingRedirect);

    // Every route INSIDE a project — miss one and the else-branch below clears the project store on
    // arrival, silently dropping phase-gated nav items (Transport, Waste) with no error.
    const projectRoutes = [
      "projectBreakdown",
      "projectSettings",
      "projectSettingsSpeckle",
      "materialMappingView",
      "transportMapping",
      "wasteSorting",
      // Entering the report is entering the project: it must reset viewer mode and seed the
      // project crumb the same way, or the report's own crumb fix has nothing to correct.
      "projectLcaPdfReportPage",
      "projectVersionHistory",
    ];

    // Reset the viewer mode only when entering a project from outside/a different project — same-project
    // tab-hops keep it. Done here (before components render) to avoid a nav-highlight flicker.
    if (projectRoutes.includes(to.name as string) && to.params.id !== from.params.id) {
      useSpeckleStore().enableViewer = false;
    }

    if (projectRoutes.includes(to.name as string)) {
      // Set breadcrumb immediately with placeholder before fetching data
      const items = resolveBreadcrumb(to);
      if (items[1]) {
        const project = projectWrapper.getProjectWrapper()?.project;
        // Only use cached project name if it matches the current route's project ID
        const projectIdMatches = project?.id === to.params.id;
        items[1] = {
          ...items[1],
          label: projectIdMatches ? project?.projectName : undefined,
        };
      }
      breadcrumb.set(items);

      // For most project routes, don't block navigation - let components handle loading
      // This provides immediate navigation feedback to users
      const immediateNavigationRoutes = [
        "projectSettings",
        "projectSettingsSpeckle",
        "projectBreakdown",
        "materialMappingView",
        "transportMapping",
        "wasteSorting",
        "projectVersionHistory",
      ];

      if (immediateNavigationRoutes.includes(to.name as string)) {
        // Unforced: the settings page forces its own refresh inside the flow provider instead, and an
        // unforced call here early-returns on the project it already holds rather than double-fetching.
        const forceRefresh = false;

        // fire-and-forget, but AUTH-GATED
        void waitForHasuraAuthenticated()
          .then(() =>
            Promise.all([
              projectWrapper.refreshProjectWrapper(projectId, forceRefresh),
              projectWrapper.refreshProjectAdditionalInfoWrapper(projectId, forceRefresh),
            ])
          )
          .catch((e) => {
            // don't crash the app; log/report
            console.error("Background project refresh skipped/failed:", e);
          });
      } else {
        // blocking routes: wait for auth *then* fetch
        await waitForHasuraAuthenticated();
        await Promise.all([
          projectWrapper.refreshProjectWrapper(projectId),
          projectWrapper.refreshProjectAdditionalInfoWrapper(projectId),
        ]);
      }
    } else {
      useProject().setProject(null);
    }

    previousRoute.value = from;
    next();
  });

  // A lazy route chunk from a previous deploy is gone; a reload fetches the current build. Sentry's
  // router hook still sees the error, so main.ts lists these messages under ignoreErrors.
  router.onError((error) => {
    recoverFromStaleAsset(error);
  });

  router.afterEach(async (to, from, failure) => {
    // Clear the reload guard only on a *successful* navigation, so a later transient chunk failure
    // can still self-recover; surface aborts to Sentry.
    if (!failure) {
      sessionStorage.removeItem("chunk_reload_attempted");
    } else if (isNavigationFailure(failure, NavigationFailureType.aborted | NavigationFailureType.cancelled)) {
      Sentry.captureMessage(`Navigation aborted to ${String(to.name)}`, {
        level: "warning",
        extra: { to: to.fullPath, from: from.fullPath },
      });
    }

    // The 3D viewer shares the dashboard's route, this helps it tell the difference.
    if (!failure && to.name === "projectBreakdown") {
      useSpeckleStore().enableViewer = to.query.view === "3d";
    }

    if (PROJECT_VIEW_ROUTE_NAMES.includes(to.name as string)) {
      const projectId = typeof to.params.id === "string" ? to.params.id : undefined;
      trackProjectView(projectId);
    }

    // Pages that still set document.title themselves mount after this hook, so they keep overriding
    // it — this only guarantees no route inherits the previous one's title.
    document.title = resolveTitle(to);

    const breadcrumb = useBreadcrumb();
    const items = resolveBreadcrumb(to);

    // Routes that use ProjectWrapper store for breadcrumb updates
    const projectWrapperRoutes = [
      "projectBreakdown",
      "projectSettings",
      "projectSettingsSpeckle",
      "transportMapping",
      "wasteSorting",
      "projectVersionHistory",
    ];

    if (projectWrapperRoutes.includes(to.name as string)) {
      const projectWrapper = useProjectWrapper();

      const project = projectWrapper.getProjectWrapper()?.project;

      if (items[1]) {
        // Only use cached project name if it matches the current route's project ID
        const projectIdMatches = project?.id === to.params.id;
        items[1] = {
          ...items[1],
          label: projectIdMatches ? project?.projectName : undefined,
        };
      }

      breadcrumb.set(items);
    } else if (to.name !== "materialMappingView") {
      // For non-project routes, set breadcrumb normally
      // materialMappingView handles its own breadcrumb in the component
      breadcrumb.set(items);
    }
  });
}

// Frontegg's auth state lands asynchronously after the first navigation and briefly reports
// isLoading: false with no user — reading the store immediately would wrongly bounce a PlatformAdmin.
const AUTH_USER_TIMEOUT_MS = 10000;

export function waitForAuthUser(): Promise<AuthStateUser | null> {
  const authStateStore = useAuthStateStore();

  return new Promise((resolve) => {
    if (authStateStore.user) return resolve(authStateStore.user);

    let stop = () => {};
    const finish = (user: AuthStateUser | null) => {
      clearTimeout(timeout);
      stop();
      resolve(user);
    };

    // Frontegg can stall (offline, expired refresh token) — don't hang navigation forever.
    const timeout = setTimeout(() => finish(authStateStore.user ?? null), AUTH_USER_TIMEOUT_MS);

    stop = watch(
      () => [authStateStore.user, authStateStore.authState?.isLoading, authStateStore.authState?.isAuthenticated],
      () => {
        const authState = authStateStore.authState;

        if (authStateStore.user) return finish(authStateStore.user);
        if (authState && !authState.isLoading && authState.isAuthenticated === false) return finish(null);
      },
      { immediate: true }
    );
  });
}

export function waitForHasuraAuthenticated(): Promise<void> {
  const hasura = useHasuraStore();

  return new Promise((resolve, reject) => {
    // already ready
    if (hasura.status === "authenticated") return resolve();
    if (hasura.status === "error") return reject(new HasuraNotAuthenticatedError());

    const stop = watch(
      () => hasura.status,
      (s) => {
        if (s === "authenticated") {
          stop();
          resolve();
        } else if (s === "error") {
          stop();
          reject(new HasuraNotAuthenticatedError());
        }
      },
      { immediate: true }
    );
  });
}

export default router;
